import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import React, { useEffect, useRef, useState } from 'react'
import { FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa'
import { FaPix } from 'react-icons/fa6'
import { useHistory } from 'react-router'
import { useFinalizeCheckout } from '../../../../../hooks/useFinalizeCheckout'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import PaymentModal from '../../../../../new-components/PaymentModal'
import { getEvents } from '../../../../../services/events'
import { PagaleveCheckout, paymentService } from '../../../../../services/paymentService'
import { WarningBanners } from '../resumeStep/WarningBanners'

interface PagalevePaymentProps {
  selectedMethod: string
  cartItems: any[]
  onPaymentStatusChange?: (status: 'processing' | 'success' | 'error') => void
}

export const PagalevePayment: React.FC<PagalevePaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  onPaymentStatusChange
}) => {
  const [pagaleveCheckout, setPagaleveCheckout] = useState<PagaleveCheckout | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [autoRedirectCountdown, setAutoRedirectCountdown] = useState(15)
  const [hasOpenedCheckout, setHasOpenedCheckout] = useState(false)
  const { lastOrderCreated } = usePrepareCart() as any
  const history = useHistory()
  const pollingRef = useRef<number | null>(null)
  const pollingTimeoutRef = useRef<number | null>(null)
  const redirectedRef = useRef<boolean>(false)
  const { finalize } = useFinalizeCheckout()

  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const isAllGeneticTests = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.type === 'TESTE_GENETICO'
  )

  const isPillOnly: boolean = lastOrderCreated?.is_pill_only ?? false
  const hasPillControlledItems = (lastOrderCreated?.items || []).some(
    (item: any) => item.requires_controlled_prescription
  )
  const skipDocuments = isAllSupplements || isAllGeneticTests || (isPillOnly && !hasPillControlledItems)

  const showPaymentStatusModal = (status: 'processing' | 'success' | 'error') => {
    setPaymentStatus(status)
    setShowPaymentModal(true)
    onPaymentStatusChange?.(status)
  }

  const handlePaymentModalAction = () => {
    setShowPaymentModal(false)
    if (paymentStatus !== 'success' || !lastOrderCreated?.id) return

    finalize({
      redirectTo: skipDocuments
        ? `/detalhes-do-pedido/${lastOrderCreated.id}`
        : `/enviar-receita/${lastOrderCreated.id}`,
      shouldClearCart: true,
      alsoClearOrderState: true
    })
  }

  async function generatePagaleve() {
    if (!lastOrderCreated?.id) return

    setIsGenerating(true)
    setError(null)

    try {
      const checkout = await paymentService.generatePagaleveCheckout(lastOrderCreated.id)
      setPagaleveCheckout(checkout)
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar PIX parcelado. Tente novamente.')
      console.error('Erro ao gerar Pagaleve:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  // Gera automaticamente o checkout ao montar
  useEffect(() => {
    if (lastOrderCreated?.id) {
      ;(async () => {
        await generatePagaleve()
      })()
    }
  }, [lastOrderCreated?.id])

  // Polling para verificar status do pagamento
  useEffect(() => {
    if (!pagaleveCheckout || !lastOrderCreated?.id) return undefined

    const tick = async () => {
      try {
        // Busca o pedido atualizado
        const orderResponse = await fetch(`/api/orders/${lastOrderCreated.id}`)
        const orderData = await orderResponse.json()

        // Verifica se algum pagamento foi confirmado
        const hasPaidPayment = orderData?.payments?.some((p: any) => p.status === 'paid')

        if (hasPaidPayment && !redirectedRef.current) {
          if (pollingRef.current) window.clearInterval(pollingRef.current)
          if (pollingTimeoutRef.current) window.clearTimeout(pollingTimeoutRef.current)
          redirectedRef.current = true

          showPaymentStatusModal('processing')

          setTimeout(() => {
            showPaymentStatusModal('success')

            getEvents.eventLoadObjects({
              name: 'checkout_redirect',
              category: 'checkout',
              action: 'pagaleve_paid_polling_redirect',
              value: String(lastOrderCreated.id)
            })
            getEvents.eventSendDataLayer({
              eventName: 'checkout_redirect',
              method: 'pagaleve',
              trigger: 'polling_paid',
              orderId: lastOrderCreated.id,
              target: skipDocuments
                ? `/detalhes-do-pedido/${lastOrderCreated.id}`
                : `/enviar-receita/${lastOrderCreated.id}`
            })
          }, 4000)
        }
      } catch {
        // ignore
      }
    }

    const id = window.setInterval(tick, 5000)
    pollingRef.current = id

    const timeoutId = window.setTimeout(
      () => {
        if (pollingRef.current !== null) {
          window.clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      },
      20 * 60 * 1000
    )
    pollingTimeoutRef.current = timeoutId

    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      if (pollingTimeoutRef.current !== null) {
        window.clearTimeout(pollingTimeoutRef.current)
        pollingTimeoutRef.current = null
      }
    }
  }, [pagaleveCheckout, lastOrderCreated?.id, history])

  const handleOpenCheckout = () => {
    if (pagaleveCheckout?.checkout_url) {
      setHasOpenedCheckout(true)
      window.location.href = pagaleveCheckout.checkout_url
    }
  }

  const handleOpenInNewTab = () => {
    if (pagaleveCheckout?.checkout_url) {
      setHasOpenedCheckout(true)
      window.open(pagaleveCheckout.checkout_url, '_blank')
    }
  }

  // Auto redirect countdown (após checkout ser criado)
  useEffect(() => {
    if (pagaleveCheckout?.checkout_url && !hasOpenedCheckout && autoRedirectCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoRedirectCountdown((prev) => prev - 1)
      }, 1000)

      return () => clearTimeout(timer)
    }

    if (autoRedirectCountdown === 0 && !hasOpenedCheckout) {
      handleOpenCheckout()
    }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagaleveCheckout, autoRedirectCountdown, hasOpenedCheckout])

  if (isGenerating) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          Gerando PIX Parcelado...
        </h2>
        <div className="mt-6 flex items-center justify-center">
          <div className="h-32 w-32 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-red-700">
          <FaPix className="h-4 w-4 text-red-700" />
          Erro ao gerar PIX Parcelado
        </h2>
        <p className="mt-2 text-red-600">{error}</p>
        <button
          onClick={generatePagaleve}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Tentar novamente
        </button>
      </div>
    )
  }

  if (!pagaleveCheckout) {
    return (
      <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-700">
          <FaPix className="h-4 w-4 text-primary-700" />
          PIX Parcelado não disponível
        </h2>
        <p className="mt-2 text-gray-600">Clique no botão para gerar o PIX parcelado</p>
        <button
          onClick={generatePagaleve}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
          Gerar PIX Parcelado
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full rounded-xl bg-white p-8 shadow-sm">
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <FaPix className="h-20 w-20 text-primary-700" />
            <FaCheckCircle className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-white text-green-500" />
          </div>
        </div>

        <h2 className="mb-2 text-2xl font-bold text-gray-800">Checkout Criado com Sucesso!</h2>

        <p className="mb-4 text-lg text-gray-600">Parcele seu PIX em até 12x</p>
        <p className="mb-4 text-sm text-gray-500">Você escolherá a quantidade de parcelas no próximo passo</p>

        {pagaleveCheckout.amount && (
          <div className="mx-auto mb-6 w-fit rounded-lg bg-primary-50 px-6 py-3">
            <p className="text-sm text-gray-600">Valor Total</p>
            <p className="text-2xl font-bold text-primary-700">
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2
              }).format(pagaleveCheckout.amount)}
            </p>
          </div>
        )}

        <div className="mb-8 rounded-lg bg-blue-50 p-6">
          <h3 className="mb-4 text-lg font-semibold text-blue-900">Como funciona o PIX Parcelado:</h3>
          <ol className="space-y-3 text-left text-sm text-blue-800">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 font-semibold text-blue-900">
                1
              </span>
              <div>
                <span className="font-semibold">Redirecionamento:</span> Você irá para o checkout seguro da Pagaleve
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 font-semibold text-blue-900">
                2
              </span>
              <div>
                <span className="font-semibold">Análise de crédito:</span> A Pagaleve analisa seu CPF (instantâneo)
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 font-semibold text-blue-900">
                3
              </span>
              <div>
                <span className="font-semibold">Escolha as parcelas:</span> Selecione quantas vezes quer parcelar (até
                12x)
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 font-semibold text-blue-900">
                4
              </span>
              <div>
                <span className="font-semibold">Pague via PIX:</span> Escaneie o QR Code e pague a primeira parcela
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 font-semibold text-blue-900">
                5
              </span>
              <div>
                <span className="font-semibold">Confirmação:</span> Você voltará aqui automaticamente após o pagamento
              </div>
            </li>
          </ol>
          <div className="mt-4 rounded-md bg-blue-100 p-3">
            <p className="text-xs text-blue-900">
              💡 <span className="font-semibold">Importante:</span> As demais parcelas serão cobradas automaticamente
              pela Pagaleve nos meses seguintes. Você paga apenas a primeira via PIX agora.
            </p>
          </div>
        </div>

        {!hasOpenedCheckout && autoRedirectCountdown > 0 ? (
          <div className="mb-6">
            <div className="mx-auto mb-4 h-2 w-full max-w-md overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-primary-600 transition-all duration-1000 ease-linear"
                style={{ width: `${((15 - autoRedirectCountdown) / 15) * 100}%` }}
              />
            </div>
            <p className="text-sm text-gray-500">
              Redirecionando para o Pagaleve em <span className="font-bold">{autoRedirectCountdown}</span> segundos...
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleOpenCheckout}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-700 px-6 py-4 text-lg font-semibold text-white transition hover:bg-primary-800">
            <FaExternalLinkAlt className="h-5 w-5" />
            {hasOpenedCheckout ? 'Abrir Pagaleve Novamente' : 'Ir para o Pagaleve'}
          </button>

          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="w-full rounded-lg border-2 border-gray-300 bg-white px-6 py-3 text-base font-medium text-gray-700 transition hover:bg-gray-50">
            Abrir em Nova Aba
          </button>
        </div>

        {hasOpenedCheckout && (
          <div className="mt-6 rounded-lg bg-green-50 p-4">
            <p className="text-sm font-semibold text-green-900">✓ Checkout aberto</p>
            <p className="mt-1 text-xs text-green-700">
              Complete o pagamento na janela do Pagaleve. Você será redirecionado automaticamente após a confirmação.
            </p>
          </div>
        )}

        <div className="mt-6 text-xs text-gray-400">
          <p>Checkout ID: {pagaleveCheckout.checkout_id}</p>
        </div>
      </div>

      <div className="mt-8">
        <WarningBanners
          containsProductWithLongerDeliveryTime={false}
          containProductsMatirz={false}
          containsProductWithHubDistributor={false}
        />
      </div>

      {/* Modal de status do pagamento */}
      {showPaymentModal && (
        <PaymentModal
          status={paymentStatus}
          onClose={() => setShowPaymentModal(false)}
          onAction={handlePaymentModalAction}
          isAllSupplements={isAllSupplements}
          isAllGeneticTests={isAllGeneticTests}
          skipDocuments={skipDocuments}
        />
      )}
    </div>
  )
}
